/**
 * Smart Item Matcher - Intelligently matches similar shopping items
 * Handles abbreviations, synonyms, and variations in item names
 */

// Common abbreviations and synonyms for grocery items
const ITEM_SYNONYMS = {
  'milk': ['milk', 'mlk', '2% milk', 'whole milk', 'skim milk', '1% milk', 'low fat milk'],
  'chocolate milk': ['choc milk', 'chocolate milk', 'cocoa milk', 'choco milk', 'chocolate lactaid', 'lactaid'],
  'bread': ['bread', 'loaf', 'neutral-50 bread', 'wheat bread', 'whole wheat bread'],
  'eggs': ['eggs', 'dozen eggs', 'large eggs', 'cage free eggs', 'organic eggs'],
  'bananas': ['bananas', 'banana', 'nanas', 'yellow bananas'],
  'apples': ['apples', 'apple', 'honeycrisp', 'honeycrisp apples','cosmic crisp', 'cosmic crisp apples', 'envy', 'envy apples', 'fuji', 'fuji apples', 'pink lady', 'pink lady apples'],
  'grapes': ['grapes', 'grape', 'red grapes', 'emerald grapes', 'cotton candy grapes'],
  'carrots': ['carrots', 'carrot', 'baby carrots', 'little carrots'],
  'lettuce': ['lettuce', 'lettuce', 'romaine lettuce', 'iceberg lettuce', 'butter lettuce', 'red leaf lettuce'],  
  'peppers': ['peppers', 'red peppers', 'emerald peppers', 'yellow peppers', 'bell peppers'],
  'onions': ['onions', 'red onions', 'emerald onions', 'yellow onions', 'neutral-50 onions', 'shallots'],
  'tomatoes': ['tomatoes', 'tomato', 'red tomatoes', 'emerald tomatoes', 'yellow tomatoes', 'cherry tomatoes'],
  'potatoes': ['potatoes', 'potato', 'red potatoes', 'neutral-50 potatoes', 'yellow potatoes', 'russet potatoes'],
  'cucumbers': ['cucumbers', 'cucumber', 'baby cucumbers', 'little cucumbers'],
  'toilet paper': ['toilet paper', 'tp', 'bathroom tissue', 'toilet tissue'],
  'paper towels': ['paper towels', 'paper towel', 'kitchen towels', 'bounty'],
  'orange juice': ['orange juice', 'oj', 'fresh oj', 'pulp free oj'],
  'peanut butter': ['peanut butter', 'pb', 'jif', 'skippy', 'natural pb'],
  'pasta': ['pasta', 'spaghetti', 'noodles', 'penne', 'rigatoni', 'linguine'],
  'cheese': ['cheese', 'shredded cheese', 'cheddar', 'mozzarella', 'swiss cheese'],
  'yogurt': ['yogurt', 'greek yogurt', 'vanilla yogurt', 'strawberry yogurt'],
  'ice cream': ['ice cream', 'frozen yogurt', 'gelato', 'vanilla ice cream'],
  'veggie burgers': ['burgers', 'impossible burgers', 'beyond burgers', 'hamburgers'],
  'pizza': ['pizza', 'frozen pizza', 'DiGiorno', 'DiGiorno pizza', 'DiGiorno frozen pizza', 'DiGiorno frozen pizza', 'DiGiorno frozen pizza'],
}
// Common abbreviations
const ABBREVIATIONS = {
  'choc': 'chocolate',
  'org': 'organic',
  'lg': 'large',
  'sm': 'small',
  'med': 'medium',
  'ff': 'fat free',
  'lf': 'low fat',
  'nf': 'non fat',
  'unsw': 'unsweetened',
  'sw': 'sweetened',
  'dec': 'decaf',
  'reg': 'regular',
  'xtra': 'extra',
  'lt': 'light',
  'hvy': 'heavy',
  'frz': 'frozen',
  'fsh': 'fresh',
  'can': 'canned',
  'btl': 'bottle',
  'pk': 'pack',
  'dz': 'dozen',
  'lb': 'pound',
  'lbs': 'pounds',
  'oz': 'ounce',
  'qt': 'quart',
  'gal': 'gallon'
};

/**
 * Normalize item name by expanding abbreviations and standardizing format
 */
export function normalizeItemName(itemName) {
  if (!itemName) return '';
  
  let normalized = itemName.toLowerCase().trim();
  
  // Expand common abbreviations
  Object.entries(ABBREVIATIONS).forEach(([abbrev, full]) => {
    const regex = new RegExp(`\\b${abbrev}\\b`, 'gi');
    normalized = normalized.replace(regex, full);
  });
  
  // Remove extra whitespace and special characters
  normalized = normalized.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
  
  return normalized;
}

/**
 * Create a fingerprint for an item based on key characteristics
 */
export function createItemFingerprint(itemName, category = '') {
  const normalized = normalizeItemName(itemName);
  const words = normalized.split(' ').filter(word => word.length > 2);
  
  // Sort words to make fingerprint order-independent
  const sortedWords = words.sort().join(' ');
  
  // Include category for better matching
  return `${category}_${sortedWords}`.toLowerCase();
}

/**
 * Calculate similarity score between two item names (0-1)
 */
export function calculateSimilarity(name1, name2) {
  if (!name1 || !name2) return 0;
  
  const norm1 = normalizeItemName(name1);
  const norm2 = normalizeItemName(name2);
  
  // Exact match after normalization
  if (norm1 === norm2) return 1.0;
  
  // Check for synonym groups
  for (const [baseItem, synonyms] of Object.entries(ITEM_SYNONYMS)) {
    if (synonyms.includes(norm1) && synonyms.includes(norm2)) {
      return 0.9; // High similarity for known synonyms
    }
  }
  
  // Jaccard similarity (intersection over union of words)
  const words1 = new Set(norm1.split(' '));
  const words2 = new Set(norm2.split(' '));
  
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  
  const jaccardSimilarity = intersection.size / union.size;
  
  // Boost similarity if one name contains the other
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    return Math.max(jaccardSimilarity, 0.7);
  }
  
  return jaccardSimilarity;
}

/**
 * Find the best matching item from a list of existing items
 */
export function findBestMatch(newItemName, existingItems, threshold = 0.6) {
  if (!newItemName || !existingItems?.length) return null;
  
  let bestMatch = null;
  let bestScore = 0;
  
  for (const item of existingItems) {
    const score = calculateSimilarity(newItemName, item.name || item.item_name);
    
    if (score > bestScore && score >= threshold) {
      bestScore = score;
      bestMatch = { ...item, similarity: score };
    }
  }
  
  return bestMatch;
}

/**
 * Group similar items together from shopping history
 */
export function groupSimilarItems(historyItems) {
  const groups = new Map();
  const processed = new Set();
  
  historyItems.forEach((item, index) => {
    if (processed.has(index)) return;
    
    const group = {
      baseItem: item,
      variants: [item],
      totalPurchases: 1,
      lastPurchased: item.purchase_date,
      averagePrice: item.purchase_price || 0,
      category: item.category_name
    };
    
    // Find similar items
    historyItems.forEach((otherItem, otherIndex) => {
      if (otherIndex <= index || processed.has(otherIndex)) return;
      
      const similarity = calculateSimilarity(item.item_name, otherItem.item_name);
      
      if (similarity >= 0.7) { // High threshold for grouping
        group.variants.push(otherItem);
        group.totalPurchases++;
        processed.add(otherIndex);
        
        // Update group metadata
        if (new Date(otherItem.purchase_date) > new Date(group.lastPurchased)) {
          group.lastPurchased = otherItem.purchase_date;
        }
        
        if (otherItem.purchase_price) {
          group.averagePrice = (group.averagePrice + otherItem.purchase_price) / 2;
        }
      }
    });
    
    // Use normalized name as group key
    const groupKey = normalizeItemName(item.item_name);
    groups.set(groupKey, group);
    processed.add(index);
  });
  
  return Array.from(groups.values())
    .sort((a, b) => b.totalPurchases - a.totalPurchases); // Most purchased first
}

/**
 * Get suggested items based on purchase frequency and patterns
 */
export function getSuggestedItems(historyItems, limit = 10) {
  const groupedItems = groupSimilarItems(historyItems);
  
  return groupedItems
    .slice(0, limit)
    .map(group => ({
      name: group.baseItem.item_name,
      normalizedName: normalizeItemName(group.baseItem.item_name),
      purchaseCount: group.totalPurchases,
      lastPurchased: group.lastPurchased,
      averagePrice: group.averagePrice,
      category: group.category,
      variants: group.variants.map(v => v.item_name),
      daysSinceLastPurchase: Math.floor(
        (new Date() - new Date(group.lastPurchased)) / (1000 * 60 * 60 * 24)
      )
    }));
}

export default {
  normalizeItemName,
  createItemFingerprint,
  calculateSimilarity,
  findBestMatch,
  groupSimilarItems,
  getSuggestedItems
};